import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, HardDrives, Broom, ArrowClockwise, SignOut, Warning, ArrowLeft } from "@phosphor-icons/react";
import { adminLogin, getStorageStats, purgeStorage } from "@/lib/api";
import { BUSINESS } from "@/lib/data";

const TOKEN_KEY = "amwap_admin_token";

const fmtBytes = (b = 0) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const StatCard = ({ label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-2xl p-6">
    <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{label}</div>
    <div className="font-display font-black text-4xl text-navy-900 mt-2 leading-none">{value}</div>
    {sub && <div className="text-sm text-slate-500 mt-2">{sub}</div>}
  </div>
);

export default function AdminStorage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
  }, []);

  const loadStats = useCallback(async (t) => {
    setLoading(true);
    try {
      const data = await getStorageStats(t);
      setStats(data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Could not load storage stats.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) loadStats(token);
  }, [token, loadStats]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const t = await adminLogin(passcode);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPasscode("");
      toast.success("Welcome back.");
    } catch {
      toast.error("Incorrect passcode.");
    } finally {
      setLoading(false);
    }
  };

  const handlePurge = async (scope) => {
    const msg =
      scope === "uncommitted"
        ? "Purge ALL unattached uploads (files not tied to a submission)? This cannot be undone."
        : "Purge abandoned uploads older than 2 hours? This cannot be undone.";
    if (!window.confirm(msg)) return;
    setBusy(scope);
    try {
      const res = await purgeStorage(token, scope);
      setStats(res.stats);
      toast.success(`Purged ${res.purged} file${res.purged === 1 ? "" : "s"}.`);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Purge failed.");
      }
    } finally {
      setBusy("");
    }
  };

  // ---- Login gate ----
  if (!token) {
    return (
      <div className="min-h-screen bg-navy-900 grain flex items-center justify-center px-5" data-testid="admin-login">
        <div className="relative z-10 w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <span className="w-14 h-14 rounded-full bg-white grid place-items-center overflow-hidden">
              <img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" />
            </span>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-navy-900 text-white grid place-items-center mb-5">
              <LockKey size={22} weight="fill" />
            </div>
            <h1 className="font-display font-black text-2xl text-navy-900 tracking-tight">Storage Admin</h1>
            <p className="text-slate-500 mt-2 text-sm">Enter your passcode to manage attachment storage.</p>
            <input
              type="password"
              data-testid="admin-passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              autoFocus
              className="w-full mt-6 rounded-xl border border-slate-300 px-4 py-3.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600"
            />
            <button
              type="submit"
              disabled={loading || !passcode}
              data-testid="admin-login-submit"
              className="w-full mt-4 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60"
            >
              {loading ? "Checking…" : "Unlock"}
            </button>
            <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-navy-900">
              <ArrowLeft size={16} /> Back to website
            </Link>
          </form>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-white ring-1 ring-slate-200 grid place-items-center overflow-hidden">
              <img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" />
            </span>
            <span className="font-display font-extrabold text-navy-900">Storage Insight</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadStats(token)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"
            >
              <ArrowClockwise size={16} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-4 py-2 text-sm font-bold hover:bg-navy-600"
            >
              <SignOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 md:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <HardDrives size={26} weight="fill" className="text-navy-600" />
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-navy-900">Attachment Storage</h1>
        </div>

        {loading && !stats ? (
          <p className="text-slate-500">Loading…</p>
        ) : stats ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard testId="stat-active" label="Active files" value={stats.active_files} sub={fmtBytes(stats.active_bytes)} />
              <StatCard testId="stat-committed" label="Attached to submissions" value={stats.committed_files} sub="Kept safe" />
              <StatCard testId="stat-uncommitted" label="Unattached uploads" value={stats.uncommitted_files} sub={fmtBytes(stats.uncommitted_bytes)} />
              <StatCard testId="stat-abandoned" label="Abandoned (>2h)" value={stats.abandoned_files} sub={fmtBytes(stats.abandoned_bytes)} />
            </div>

            <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-7">
              <div className="flex items-center gap-3">
                <Broom size={22} weight="fill" className="text-navy-600" />
                <h2 className="font-display font-bold text-xl text-navy-900">Purge files</h2>
              </div>
              <p className="text-slate-500 mt-2 text-sm max-w-2xl">
                Files attached to a real quote or message are never purged. These tools only remove uploads that were
                started but never submitted.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={() => handlePurge("abandoned")}
                  disabled={busy !== "" || stats.abandoned_files === 0}
                  data-testid="purge-abandoned"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white px-6 py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-50"
                >
                  {busy === "abandoned" ? "Purging…" : `Purge abandoned (>2h) · ${stats.abandoned_files}`}
                </button>
                <button
                  onClick={() => handlePurge("uncommitted")}
                  disabled={busy !== "" || stats.uncommitted_files === 0}
                  data-testid="purge-uncommitted"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-red-500 text-red-600 px-6 py-3.5 font-bold hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                >
                  <Warning size={18} weight="fill" />
                  {busy === "uncommitted" ? "Purging…" : `Purge ALL unattached · ${stats.uncommitted_files}`}
                </button>
              </div>
            </div>

            <div className="mt-6 text-sm text-slate-400">
              Total purged (all-time, soft-deleted): {stats.purged_files}
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
